/**
 * LA COUCHE DE MESURE — ce que le site DIT à Google Tag Manager.
 *
 * POURQUOI CE MODULE EXISTE. Le conteneur `GTM-MQX49TJZ` est chargé par
 * `index.html` sur toutes les pages, prérendues comprises. Mais le site ne lui
 * parlait pas : `rg dataLayer src/` ne renvoyait rien. Deux conséquences
 * mesurées dans GA4 (propriété « Aikka ») entre juin et septembre 2026 :
 *
 *   · 1 321 des 1 401 pages vues de aikka.ai étaient sur `/`, et neuf chemins
 *     distincts en tout. `App.tsx` monte un `BrowserRouter` : après le premier
 *     chargement, changer de route ne recharge pas le document, donc aucun
 *     `page_view` ne partait. Toute la navigation — /store, /products/*,
 *     /contact — était invisible. C'est le trou que `pushVirtualPageview` ferme.
 *   · le choix du bandeau cookies restait dans `localStorage` et n'arrivait
 *     jamais jusqu'à GTM. Un tag GA4 posé derrière le Consent Mode ne voyait
 *     donc jamais passer l'accord du visiteur. C'est ce que
 *     `pushConsentUpdate` corrige.
 *
 * LA FRONTIÈRE. Ce module ne contient AUCUN identifiant de mesure et ne charge
 * aucun script tiers : il pousse des événements dans `dataLayer`, et c'est le
 * conteneur qui décide quels tags s'y abonnent. C'est la raison d'être de GTM —
 * changer de destination ne doit pas demander un déploiement du front.
 *
 * `dataLayer` peut ne pas exister : un bloqueur de contenu empêche le snippet
 * GTM de s'exécuter, et le prérendu tourne sous Node sans `window`. Toutes les
 * fonctions ci-dessous sont donc silencieusement inertes dans ce cas — la
 * mesure d'audience ne casse jamais une page.
 */

import type { CookieConsent } from './cookieConsent';
import { gtagQueue } from './gtag';

/** Nom de l'événement de page vue SPA. Le déclencheur GTM porte le même. */
export const VIRTUAL_PAGEVIEW = 'virtual_pageview';

/** Nom de l'événement de mise à jour du consentement. */
export const CONSENT_UPDATE = 'aikka_consent_update';

interface DataLayerWindow extends Window {
  dataLayer?: unknown[];
}

/**
 * La file GTM, ou `null` hors navigateur.
 *
 * On ne la CRÉE pas si elle est absente : une file que personne ne consomme
 * accumulerait des événements en mémoire jusqu'à la fin de la session, et
 * l'absence de `dataLayer` signifie précisément que le snippet n'a pas tourné.
 */
function dataLayer(): unknown[] | null {
  if (typeof window === 'undefined') return null;
  const queue = (window as DataLayerWindow).dataLayer;
  return Array.isArray(queue) ? queue : null;
}

/** Pousse un objet dans `dataLayer`. Sans file, ne fait rien et le dit. */
export function push(event: Record<string, unknown>): boolean {
  const queue = dataLayer();
  if (!queue) return false;
  queue.push(event);
  return true;
}

/**
 * Une page vue de navigation côté client.
 *
 * `page_path` est le chemin AVEC sa query et son fragment retirés du titre mais
 * conservés dans le chemin : c'est ce que GA4 attend, et c'est aussi ce qui
 * distingue `/store?plan=pro` de `/store` dans les rapports. Le titre est lu
 * sur le document plutôt que passé par l'appelant, parce que c'est `<Seo>` qui
 * le pose et qu'il est donc déjà à jour au moment où l'effet tourne.
 */
export function pushVirtualPageview(location: { pathname: string; search?: string; hash?: string }): boolean {
  const path = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  return push({
    event: VIRTUAL_PAGEVIEW,
    page_path: path,
    page_location: typeof window === 'undefined' ? path : window.location.href,
    page_title: typeof document === 'undefined' ? '' : document.title,
    page_language: typeof document === 'undefined' ? '' : document.documentElement.lang,
  });
}

/**
 * Relaie le choix du bandeau vers le Consent Mode de Google.
 *
 * La correspondance est celle des catégories de `cookieConsent.ts`, et elle est
 * VOLONTAIREMENT étroite : seule la catégorie `analytics` ouvre
 * `analytics_storage`. Le stockage publicitaire reste refusé en toutes
 * circonstances — le bandeau ne demande pas d'accord publicitaire, donc le site
 * n'a rien à accorder au nom du visiteur. Le jour où une catégorie `marketing`
 * apparaîtra, elle se branchera ici, et pas avant.
 *
 * `consent === null` (choix effacé depuis « Gérer les cookies ») retire
 * l'accord : c'est le retrait que le RGPD exige aussi simple que l'accord.
 *
 * DEUX COMMANDES, DEUX DESTINATAIRES. L'événement `aikka_consent_update`
 * reste poussé tel quel : c'est lui qu'un déclencheur GTM peut écouter (le
 * conteneur décide alors quoi en faire). Mais depuis que le site pose ses
 * propres tags gtag.js — GA4 (`components/layout/GoogleAnalyticsTag.tsx`),
 * Google Ads de l'agent — l'accord doit leur parvenir SANS détour par GTM :
 * la vraie commande `gtag('consent', 'update')` est donc poussée dans la même
 * file, que chaque instance gtag.js partage. Sans elle, un visiteur qui
 * accepte resterait mesuré sans cookie pour toujours — le consentement ne
 * pourrait plus jamais s'ouvrir, seulement se fermer. `ad_storage` etc.
 * restent `denied` : le bandeau ne les accorde pas, l'update non plus.
 */
export function pushConsentUpdate(consent: CookieConsent | null): boolean {
  const granted = consent?.analytics === true;
  // Le nom n'est pas anodin : `push` est déjà la fonction de ce module. La
  // file gtag s'appelle donc `gtag`, et l'événement part par `push`.
  const gtag = gtagQueue();
  if (gtag) {
    gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
  return push({
    event: CONSENT_UPDATE,
    consent_analytics: granted ? 'granted' : 'denied',
    consent_ad_storage: 'denied',
    consent_decided: consent !== null,
  });
}
